/**
 * Verification page – level progress, email & KYC verification (dashboard-wt theme)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import { useI18n } from '../providers';
import { UserDashboardLayout } from '../components/user';
import EmailVerification from '../components/auth/EmailVerification';
import KYCVerification from '../components/user/KYCVerification';
import StatusBadge from '../components/common/StatusBadge';
import Spinner from '../components/common/Spinner';
import { ROUTES } from '../utils/constants';
import '../styles/landing.css';
import '../styles/animations.css';
import '../styles/global.css';

const Verification = () => {
  const navigate = useNavigate();
  const { user: userFromApi, loadMyProfile, loading: profileLoading } = useUser();
  const { language } = useI18n();
  const [activeTab, setActiveTab] = useState('email');

  const profile = userFromApi?.data?.user ?? userFromApi?.user ?? userFromApi ?? null;
  const level = Number(profile?.verification_level ?? profile?.verificationLevel ?? 0);

  useEffect(() => {
    loadMyProfile();
  }, [loadMyProfile]);

  const onProfileUpdated = () => {
    loadMyProfile();
  };

  const levels = [
    {
      level: 0,
      name: language === 'fr' ? 'Niveau 0' : 'Level 0',
      title: language === 'fr' ? 'Téléphone vérifié' : 'Phone Verified',
      description: language === 'fr' ? 'Accès de base' : 'Basic access',
      icon: '📱',
      status: level >= 0 ? 'completed' : 'pending',
      color: 'from-gray-500 to-slate-500',
      tab: null,
    },
    {
      level: 1,
      name: language === 'fr' ? 'Niveau 1' : 'Level 1',
      title: language === 'fr' ? 'Email vérifié' : 'Email Verified',
      description: language === 'fr' ? 'Sécurité et reçus' : 'Security & receipts',
      icon: '📧',
      status: level >= 1 ? 'completed' : level === 0 ? 'available' : 'locked',
      color: 'from-blue-500 to-cyan-500',
      tab: 'email',
    },
    {
      level: 2,
      name: language === 'fr' ? 'Niveau 2' : 'Level 2',
      title: language === 'fr' ? 'KYC vérifié' : 'KYC Verified',
      description: language === 'fr' ? 'Accès complet' : 'Full access',
      icon: '🆔',
      status: level >= 2 ? 'completed' : level === 1 ? 'available' : 'locked',
      color: 'from-green-500 to-emerald-500',
      tab: 'kyc',
    },
  ];

  const handleLevelClick = (l) => {
    if (l.status === 'available' && l.tab) setActiveTab(l.tab);
  };

  if (profileLoading && !profile) {
    return (
      <UserDashboardLayout>
        <div className="verification-wt dashboard-wt min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <Spinner size="lg" className="mx-auto" />
            <p className="mt-4 text-[#a3a3a3]">
              {language === 'fr' ? 'Chargement du statut...' : 'Loading verification status...'}
            </p>
          </div>
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div className="verification-wt dashboard-wt">
        {/* Hero */}
        <section className="dashboard-wt-hero">
          <div className="dashboard-wt-hero-bg" aria-hidden="true" />
          <div className="dashboard-wt-hero-grid" aria-hidden="true" />
          <div className="dashboard-wt-hero-noise" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-1" aria-hidden="true" />
          <div className="dashboard-wt-hero-orb dashboard-wt-hero-orb-2" aria-hidden="true" />
          <div className="dashboard-wt-hero-inner">
            <h1 className="dashboard-wt-hero-title">
              {language === 'fr' ? 'Vérification' : 'Verification'}
            </h1>
            <p className="dashboard-wt-hero-sub">
              {language === 'fr'
                ? 'Débloquez plus de fonctionnalités en vérifiant votre compte'
                : 'Unlock more features by verifying your account'}
            </p>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[rgba(34,197,94,0.15)] text-[#22c55e] text-sm font-medium">
                {language === 'fr' ? 'Votre niveau' : 'Your level'}: <strong>Level {level}</strong>
              </span>
              <button
                type="button"
                className="dashboard-wt-btn-secondary py-2 px-4"
                onClick={() => navigate(ROUTES.DASHBOARD)}
              >
                ← {language === 'fr' ? 'Retour' : 'Back'}
              </button>
            </div>
          </div>
        </section>

        {/* Level cards – clickable when available */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <h2 className="dashboard-wt-card-title mb-4 text-[#e5e5e5]">
              {language === 'fr' ? 'Niveaux de vérification' : 'Verification levels'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {levels.map((l, index) => (
                <button
                  type="button"
                  key={l.level}
                  onClick={() => handleLevelClick(l)}
                  disabled={l.status !== 'available'}
                  className={`w-full text-left dashboard-wt-card p-5 rounded-xl border transition-all animate-fadeInUp ${
                    l.status === 'completed'
                      ? 'border-[rgba(34,197,94,0.35)] bg-[rgba(34,197,94,0.06)]'
                      : l.status === 'available'
                        ? 'border-[rgba(59,130,246,0.35)] bg-[rgba(59,130,246,0.06)] hover:border-[rgba(59,130,246,0.5)] cursor-pointer'
                        : 'border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)] cursor-default opacity-90'
                  }`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${l.color} flex items-center justify-center text-2xl shadow-lg`}>
                      {l.icon}
                    </div>
                    {l.status === 'completed' && (
                      <StatusBadge variant="success" size="sm">
                        ✓ {language === 'fr' ? 'Complété' : 'Completed'}
                      </StatusBadge>
                    )}
                    {l.status === 'available' && (
                      <StatusBadge variant="info" size="sm">
                        {language === 'fr' ? 'Cliquez pour continuer' : 'Click to continue'}
                      </StatusBadge>
                    )}
                    {l.status === 'locked' && (
                      <StatusBadge variant="default" size="sm">
                        🔒 {language === 'fr' ? 'Verrouillé' : 'Locked'}
                      </StatusBadge>
                    )}
                  </div>
                  <h3 className="dashboard-wt-card-title text-lg mb-1">{l.name}</h3>
                  <p className="font-semibold text-[#e5e5e5] mb-1">{l.title}</p>
                  <p className="text-sm text-[#a3a3a3]">{l.description}</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Tabs */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className="dashboard-wt-card p-2 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)]">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setActiveTab('email')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all ${
                    activeTab === 'email'
                      ? 'bg-[rgba(34,197,94,0.15)] text-[#22c55e] border border-[rgba(34,197,94,0.3)]'
                      : 'text-[#a3a3a3] hover:bg-[rgba(255,255,255,0.05)] border border-transparent'
                  }`}
                >
                  <span>📧</span>
                  {language === 'fr' ? 'Email' : 'Email'}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('kyc')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all ${
                    activeTab === 'kyc'
                      ? 'bg-[rgba(34,197,94,0.15)] text-[#22c55e] border border-[rgba(34,197,94,0.3)]'
                      : 'text-[#a3a3a3] hover:bg-[rgba(255,255,255,0.05)] border border-transparent'
                  }`}
                >
                  <span>🆔</span>
                  {language === 'fr' ? 'KYC' : 'KYC'}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Tab content */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className="dashboard-wt-card p-6 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--wt-black-2,#111)]">
              {activeTab === 'email' ? (
                <EmailVerification onVerified={onProfileUpdated} />
              ) : (
                <KYCVerification onProfileUpdated={onProfileUpdated} />
              )}
            </div>
          </div>
        </section>

        {/* Benefits – compact */}
        <section className="dashboard-wt-section">
          <div className="dashboard-wt-section-inner">
            <div className="dashboard-wt-card p-5 rounded-xl border border-[rgba(34,197,94,0.2)] bg-[rgba(34,197,94,0.04)]">
              <h3 className="dashboard-wt-card-title mb-3 flex items-center gap-2 text-[#e5e5e5]">
                <span>✨</span>
                {language === 'fr' ? 'Avantages' : 'Benefits'}
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-sm text-[#e5e5e5]">
                <li className="flex items-center gap-2">
                  <span className="text-[#22c55e]">✓</span>
                  {language === 'fr' ? 'Limites plus élevées' : 'Higher limits'}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#22c55e]">✓</span>
                  {language === 'fr' ? 'Score de confiance' : 'Trust score'}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#22c55e]">✓</span>
                  {language === 'fr' ? 'Support prioritaire' : 'Priority support'}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#22c55e]">✓</span>
                  {language === 'fr' ? 'Fonctionnalités premium' : 'Premium features'}
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#22c55e]">✓</span>
                  {language === 'fr' ? 'Résolution des litiges' : 'Dispute resolution'}
                </li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </UserDashboardLayout>
  );
};

export default Verification;
